import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TierFeatureKey, type PrivateLessonRequest } from '@court-sync/shared';
import { privateBookingService } from '../../../../services/privateBooking';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/ui';
import toast from 'react-hot-toast';
import { Copy, Inbox } from 'lucide-react';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { formatTimeHHMM } from '../../../../utils/timeFormat';
import { useCourts } from '../../../../hooks/useDataStore';
import { BOOKINGS_MANAGEMENT_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { useManagementDashboardFeatureAccess } from '@/hooks/useFeatureAccess';

const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatYmdLong(ymd: string): string {
  try {
    const d = new Date(`${ymd}T12:00:00`);
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    }).format(d);
  } catch {
    return ymd;
  }
}

function scheduleSummary(r: PrivateLessonRequest): string {
  if (r.sessionSlots && r.sessionSlots.length > 0) {
    const sorted = [...r.sessionSlots].sort(
      (a, b) =>
        a.sessionDate.localeCompare(b.sessionDate) || a.startTime.localeCompare(b.startTime)
    );
    return sorted
      .map(
        (s) =>
          `${formatYmdLong(s.sessionDate)} · ${formatTimeHHMM(s.startTime)} – ${formatTimeHHMM(s.endTime)}`
      )
      .join('\n');
  }
  const day = WEEKDAY_LONG[r.dayOfWeek] ?? `Day ${r.dayOfWeek}`;
  return `${day} · ${formatTimeHHMM(r.startTime)} – ${formatTimeHHMM(r.endTime)}${
    r.numberOfSessions > 1 ? ` · ${r.numberOfSessions} sessions` : ''
  }`;
}

function StatusBadge({ status }: { status: PrivateLessonRequest['status'] }) {
  switch (status) {
    case 'pending_approval':
      return <Badge variant="warning">Pending approval</Badge>;
    case 'approved':
      return <Badge variant="success">Approved</Badge>;
    case 'rejected':
      return <Badge variant="destructive">Rejected</Badge>;
    case 'confirmed':
      return <Badge variant="secondary">Confirmed</Badge>;
    case 'cancelled':
      return <Badge variant="outline">Cancelled</Badge>;
    case 'expired':
      return <Badge variant="outline">Expired</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

type Props = {
  academyId: string | undefined;
  academySlug?: string;
  /** When true, show "Copy payment link" for approved requests awaiting online payment */
  canTakeOnlinePayments?: boolean;
};

function buildGuardianPaymentUrl(academySlug: string, lessonId: string, bookingId: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}/academy/${encodeURIComponent(academySlug)}/book-lesson/${encodeURIComponent(lessonId)}?bookingId=${encodeURIComponent(bookingId)}`;
}

export function PrivateLessonRequestsPanel({
  academyId,
  academySlug,
  canTakeOnlinePayments,
}: Props) {
  const { hasAccess: hasSelfServiceBookingAccess } = useManagementDashboardFeatureAccess(
    TierFeatureKey.STUDENT_SELF_SERVICE_BOOKING
  );
  const [rows, setRows] = useState<PrivateLessonRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectRequestId, setRejectRequestId] = useState<string | null>(null);
  const [rejectMessage, setRejectMessage] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveRequestId, setApproveRequestId] = useState<string | null>(null);
  const [approveCourtId, setApproveCourtId] = useState<string>('');
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const { courts } = useCourts(academyId);
  const activeCourts = useMemo(() => courts.filter((c) => c.isActive), [courts]);

  const load = async () => {
    if (!academyId || !hasSelfServiceBookingAccess) return;
    try {
      setLoading(true);
      const data = await privateBookingService.listAcademyRequests(academyId);
      setRows(data);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!academyId || !hasSelfServiceBookingAccess) return;
    void load();
  }, [academyId, hasSelfServiceBookingAccess]);

  if (!academyId) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  const openApprove = (id: string) => {
    setApproveRequestId(id);
    setApproveCourtId(activeCourts[0]?.id ?? '');
    setApproveOpen(true);
  };

  const confirmApprove = async () => {
    if (!approveRequestId) return;
    const courtId = approveCourtId || activeCourts[0]?.id;
    if (!courtId) return;
    setApproveSubmitting(true);
    try {
      await privateBookingService.approveRequest(approveRequestId, courtId);
      toast.success('Approved');
      setApproveOpen(false);
      setApproveRequestId(null);
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed');
    } finally {
      setApproveSubmitting(false);
    }
  };

  const openReject = (id: string) => {
    setRejectRequestId(id);
    setRejectMessage('');
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    if (!rejectRequestId) return;
    const reason = rejectMessage.trim() || 'Rejected';
    setRejectSubmitting(true);
    try {
      await privateBookingService.rejectRequest(rejectRequestId, reason);
      toast.success('Rejected');
      setRejectOpen(false);
      setRejectRequestId(null);
      setRejectMessage('');
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed');
    } finally {
      setRejectSubmitting(false);
    }
  };

  const copyPaymentLink = async (r: PrivateLessonRequest) => {
    if (!academySlug || !r.lessonId || !r.bookingId) {
      toast.error('Missing link details — refresh the page and try again.');
      return;
    }
    const url = buildGuardianPaymentUrl(academySlug, r.lessonId, r.bookingId);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Payment link copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="space-y-4">
      <p
        id={BOOKINGS_MANAGEMENT_TOUR_STEP_IDS.PRIVATE_INTRO}
        className="text-sm text-muted-foreground border border-border/60 rounded-md p-3 bg-muted/30"
      >
        To create a private lesson booking for a student from the academy side, use the{' '}
        <Link to="/admin/calendar" className="font-medium text-primary underline-offset-2 hover:underline">
          Calendar
        </Link>{' '}
        page (manual private scheduling). Incoming requests here are started by students on your public site.
      </p>

      <div id={BOOKINGS_MANAGEMENT_TOUR_STEP_IDS.PRIVATE_LIST}>
      {loading ? (
        <p className="text-muted-foreground px-1">Loading…</p>
      ) : rows.length === 0 ? (
        <DashboardEmptyStateCard>
          <DashboardEmptyStateIcon icon={Inbox} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No private lesson requests yet
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            When guardians request a private lesson package on your public site, they appear here for
            your approval.
          </p>
        </DashboardEmptyStateCard>
      ) : (
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Pending and recent</h2>
            <p className="text-sm text-muted-foreground">
              Student-initiated private lesson packages awaiting your approval or already processed.
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Schedule</th>
                    <th className="py-2 pr-4">Sessions</th>
                    <th className="py-2 pr-4">Amount</th>
                    <th className="py-2 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-border/60">
                      <td className="py-2 pr-4 align-top">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="py-2 pr-4 align-top whitespace-pre-line text-muted-foreground max-w-[min(420px,55vw)]">
                        {scheduleSummary(r)}
                      </td>
                      <td className="py-2 pr-4 align-top">{r.numberOfSessions}</td>
                      <td className="py-2 pr-4">
                        {r.currency} {r.totalAmount.toFixed(2)}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex flex-wrap gap-2">
                          {r.status === 'pending_approval' && (
                            <>
                              <Button size="sm" onClick={() => openApprove(r.id)}>
                                Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openReject(r.id)}>
                                Reject
                              </Button>
                            </>
                          )}
                          {r.status === 'approved' &&
                            canTakeOnlinePayments &&
                            academySlug &&
                            r.lessonId &&
                            r.bookingId &&
                            r.totalAmount > 0 && (
                              <Button
                                size="sm"
                                variant="secondary"
                                className="gap-1"
                                onClick={() => void copyPaymentLink(r)}
                              >
                                <Copy className="h-3.5 w-3.5" aria-hidden />
                                Copy payment link
                              </Button>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      </div>

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Approve private lesson request</DialogTitle>
            <DialogDescription>
              Select the court for this booking. The student will see this court in the approval email.
            </DialogDescription>
          </DialogHeader>
          {activeCourts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Add at least one active court under{' '}
              <Link to="/admin/courts" className="font-medium text-primary underline-offset-2 hover:underline">
                Courts
              </Link>{' '}
              before approving.
            </p>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="approve-court">Court</Label>
              <Select
                value={approveCourtId || activeCourts[0]?.id || ''}
                onValueChange={setApproveCourtId}
              >
                <SelectTrigger id="approve-court" className="w-full">
                  <SelectValue placeholder="Choose a court" />
                </SelectTrigger>
                <SelectContent>
                  {activeCourts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setApproveOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void confirmApprove()}
              disabled={
                approveSubmitting || activeCourts.length === 0 || !(approveCourtId || activeCourts[0]?.id)
              }
            >
              {approveSubmitting ? 'Approving…' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject private lesson request</DialogTitle>
            <DialogDescription>
              Optionally add a message for the guardian. It will be included in the rejection email. Leave blank to use
              a generic notice only.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectMessage}
            onChange={(e) => setRejectMessage(e.target.value)}
            placeholder="e.g. We don’t have capacity on that day this term — please try Tuesday mornings."
            rows={4}
            className="resize-y min-h-[100px]"
            aria-label="Message to the guardian"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={() => void confirmReject()} disabled={rejectSubmitting}>
              {rejectSubmitting ? 'Rejecting…' : 'Reject request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
